// Traslados — Multi Clasificador (Ampliado).
//
// Copia de la pantalla Multi Clasificador con una vuelta de tuerca: cuando el
// clasificador seleccionado es el PRINCIPAL de la empresa (p.ej. REFERENCIA),
// un check agrega al detalle los artículos del mismo tipo que NO se trasladan
// pero conservan saldo disponible en la bodega origen. Aparecen con cantidad 0
// para que el usuario decida si los suma al traslado.
import React, { useState, useEffect, useCallback } from "react";
import { Zap, Download } from "lucide-react";
import {
  COLORS, apiFetch, inputStyle, labelStyle, btnPrimary, btnSecondary,
  btnSmallSuccess, btnSmallDanger, btnSmallInfo, thStyle, tdStyle, mono,
  cardStyle, tableWrap, downloadCSV, CSVButton, PageHeader, StatusBadge,
} from "../shared/ui";
import MultiClassifierFilter from "../shared/MultiClassifierFilter";
import { ordenarDetalleConSaldo } from "./detalleConSaldo";

const esExtra = (d) => d.en_traslado === false;

function TrasladosMultiClasSaldoPage({ token }) {
  const [traslados, setTraslados] = useState(null);
  const [clasFilters, setClasFilters] = useState([]); // [{ id_clasificador, id_tipo }]
  const [idClasPrincipal, setIdClasPrincipal] = useState(null);
  const [incluirSaldo, setIncluirSaldo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [csvLoading, setCsvLoading] = useState(false);
  const PAGE_SIZE = 20;

  // Qué clasificador es el principal de la empresa: sin él no hay tipo por el
  // cual expandir y el check queda deshabilitado.
  useEffect(() => {
    apiFetch("/clasificadores", {}, token)
      .then(res => {
        const lista = Array.isArray(res) ? res : [];
        const principal = lista.find(c => c.principal === "S");
        setIdClasPrincipal(principal ? principal.id_clasificador : null);
      })
      .catch(() => setIdClasPrincipal(null));
  }, [token]);

  // La posición del principal entre los clasificadores seleccionados es la
  // misma que usa el backend para nombrar la columna nombre_tipo_N.
  const idxPrincipal = idClasPrincipal
    ? clasFilters.findIndex(f => f.id_clasificador === idClasPrincipal)
    : -1;
  const puedeExpandir = idxPrincipal >= 0;
  const expandiendo = puedeExpandir && incluirSaldo;

  // Build mclas[] params from clasFilters
  const buildMclasParams = (params) => {
    clasFilters.forEach(f => {
      if (f.id_clasificador) {
        const val = f.id_tipo ? `${f.id_clasificador}:${f.id_tipo}` : f.id_clasificador;
        params.append("mclas", val);
      }
    });
    if (expandiendo) params.set("incluir_saldo_origen", "1");
    return params;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: PAGE_SIZE });
      if (estadoFilter) params.set("estado", estadoFilter);
      buildMclasParams(params);
      const res = await apiFetch(`/traslados?${params}`, {}, token);
      setTraslados(res); setExpanded({});
    }
    catch (e) { console.error(e); }
    setLoading(false);
  }, [token, estadoFilter, clasFilters, page, expandiendo]);

  useEffect(() => { setPage(1); }, [estadoFilter, clasFilters, expandiendo]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const activeClas = clasFilters.filter(f => f.id_clasificador);
      if (activeClas.length > 0) {
        const params = new URLSearchParams();
        activeClas.forEach(f => {
          params.append("mclas", f.id_tipo ? `${f.id_clasificador}:${f.id_tipo}` : f.id_clasificador);
        });
        await apiFetch(`/ml/suggest-transfers-by-classifiers?${params}`, { method: "POST" }, token);
      } else {
        await apiFetch("/ml/suggest-transfers", { method: "POST" }, token);
      }
      load();
    } catch (e) { alert(e.message); }
    setGenerating(false);
  };

  const handleAction = async (id, action) => {
    if (action === "cancelar" && !window.confirm("¿Cancelar este traslado? No se puede deshacer.")) return;
    try { await apiFetch(`/traslados/${id}/${action}`, { method: "PUT" }, token); load(); }
    catch (e) { alert(e.message); }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchAllAndDownload = async (tipo) => {
    setCsvLoading(tipo);
    try {
      const SIZE = 100;
      const buildCsvParams = (p) => {
        const params = new URLSearchParams({ page: p, size: SIZE });
        if (estadoFilter) params.set("estado", estadoFilter);
        buildMclasParams(params);
        return params;
      };
      const firstRes = await apiFetch(`/traslados?${buildCsvParams(1)}`, {}, token);
      const total = Number(firstRes.total) || 0;
      let rows = firstRes.data || [];
      const totalPages = Math.ceil(total / SIZE);
      for (let p = 2; p <= totalPages; p++) {
        const res = await apiFetch(`/traslados?${buildCsvParams(p)}`, {}, token);
        rows = rows.concat(res.data || []);
      }
      if (rows.length === 0) {
        alert("No hay traslados para exportar con los filtros actuales.");
        setCsvLoading(false); return;
      }
      if (tipo === "resumen") {
        const data = rows.map(t => {
          const det = t.detalle || [];
          return {
            origen: t.origen_desc || t.id_bodega_origen,
            destino: t.destino_desc || t.id_bodega_destino,
            estado: t.estado, prioridad: t.score_prioridad,
            total_articulos: det.filter(d => !esExtra(d)).length,
            articulos_con_saldo_origen: det.filter(esExtra).length,
            fecha_sugerencia: t.fecha_sugerencia?.slice(0, 10) || "",
            fecha_aprobacion: t.fecha_aprobacion?.slice(0, 10) || "",
            fecha_recepcion: t.fecha_recepcion?.slice(0, 10) || "",
          };
        });
        downloadCSV(data, "traslados_multi_saldo_resumen");
      } else {
        const detalle = [];
        rows.forEach(t => {
          ordenarDetalleConSaldo(t.detalle || [], expandiendo ? idxPrincipal : null).forEach(d => {
            const row = {
              id_traslado: t.id_traslado,
              bodega_origen: t.id_bodega_origen, origen_desc: t.origen_desc || "",
              bodega_destino: t.id_bodega_destino, destino_desc: t.destino_desc || "",
              estado: t.estado, id_articulo: d.id_articulo,
              en_traslado: esExtra(d) ? "N" : "S",
              cantidad: d.cantidad, razon: d.razon || "",
              saldo_origen: d.saldo_origen ?? "",
              en_transito_origen: d.en_transito_origen ?? "",
              disponible_origen: d.stock_origen ?? "",
              saldo_destino: d.saldo_destino ?? "",
              prioridad: t.score_prioridad,
              fecha_sugerencia: t.fecha_sugerencia?.slice(0, 10) || "",
            };
            // Append tipo columns for each selected classifier
            clasFilters.forEach((f, i) => {
              if (f.id_clasificador) {
                row[`tipo_${f.id_clasificador}`] = d[`nombre_tipo_${i}`] || "";
              }
            });
            detalle.push(row);
          });
        });
        if (detalle.length === 0) {
          alert("No se encontraron artículos en los traslados.");
          setCsvLoading(false); return;
        }
        downloadCSV(detalle, "traslados_multi_saldo_detalle_erp");
      }
    } catch (e) {
      alert(`Error al generar CSV: ${e.message || "Error desconocido"}`);
    }
    setCsvLoading(false);
  };

  const btnRecibir = btnSmallInfo;

  // Build a label for active classifier filters
  const filterLabel = clasFilters.length > 0
    ? clasFilters.map(f => f.id_tipo ? `${f.id_clasificador}:${f.id_tipo}` : f.id_clasificador).join(" + ")
    : null;

  const tituloCheck = puedeExpandir
    ? `Agrega los artículos del clasificador ${idClasPrincipal} que comparten tipo con lo que se traslada y conservan saldo disponible en la bodega origen`
    : (idClasPrincipal
      ? `Seleccione el clasificador principal (${idClasPrincipal}) para habilitar esta opción`
      : "La empresa no tiene un clasificador principal definido");

  return (
    <div>
      <PageHeader
        title="Traslados — Multi Clasificador (Ampliado)"
        subtitle="Filtre por combinación de clasificadores y vea también lo que se queda en la bodega origen"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => fetchAllAndDownload("resumen")} disabled={!!csvLoading} style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, opacity: csvLoading ? 0.6 : 1 }}>
              <Download size={14} /> {csvLoading === "resumen" ? "Descargando..." : "Resumen CSV"}
            </button>
            <button onClick={() => fetchAllAndDownload("detalle")} disabled={!!csvLoading} style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, opacity: csvLoading ? 0.6 : 1 }}>
              <Download size={14} /> {csvLoading === "detalle" ? "Descargando..." : "Detalle ERP CSV"}
            </button>
          </div>
        }
      />

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
          <MultiClassifierFilter token={token} onChange={setClasFilters} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <label style={labelStyle}>Estado</label>
              <select value={estadoFilter} onChange={e => setEstadoFilter(e.target.value)} style={{ ...inputStyle, width: 160 }}>
                <option value="">Todos</option>
                <option value="sugerido">Sugeridos</option>
                <option value="aprobado">Aprobados</option>
                <option value="recibido">Recibidos</option>
                <option value="recibido_por_tiempo">Recibidos (tiempo)</option>
              </select>
            </div>
            <button onClick={generate} disabled={generating} style={btnPrimary}>
              <Zap size={14} /> {generating ? "Generando..." : "Generar Sugerencias ML"}
            </button>
          </div>
        </div>

        <label
          title={tituloCheck}
          style={{
            marginTop: 14, display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 13, color: puedeExpandir ? COLORS.text : COLORS.textDim,
            cursor: puedeExpandir ? "pointer" : "not-allowed",
          }}>
          <input
            type="checkbox"
            checked={expandiendo}
            disabled={!puedeExpandir}
            onChange={e => setIncluirSaldo(e.target.checked)}
          />
          Mostrar también los artículos con saldo en origen que no se trasladan
        </label>

        {filterLabel && (
          <div style={{ marginTop: 12, padding: "8px 12px", background: `${COLORS.primary}12`, borderRadius: 8, fontSize: 12, color: COLORS.primary }}>
            Filtrando por: <strong>{filterLabel}</strong>
            {expandiendo && <> · ampliado por <strong>{idClasPrincipal}</strong></>}
          </div>
        )}
      </div>

      <div style={tableWrap}><div style={{ maxHeight: 700, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: COLORS.surfaceHover, position: "sticky", top: 0 }}>
            {["", "Origen", "Destino", "Estado", "Prioridad", "Artículos", "Fecha", "Acciones"].map(h => (
              <th key={h} style={{ ...thStyle, ...(h === "" ? { width: 36 } : {}) }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{loading
            ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: COLORS.textMuted }}>Cargando...</td></tr>
            : (traslados?.data || []).map((t, i) => {
              const est = (t.estado || "").toLowerCase().replace(/\s/g, "_");
              const isOpen = expanded[t.id_traslado];
              const detalle = ordenarDetalleConSaldo(t.detalle || [], expandiendo ? idxPrincipal : null);
              const aTrasladar = detalle.filter(d => !esExtra(d));
              const seQuedan = detalle.filter(esExtra);
              return [
                <tr key={`h-${i}`}
                  style={{ borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", background: isOpen ? `${COLORS.primary}08` : "transparent" }}
                  onClick={() => toggleExpand(t.id_traslado)}>
                  <td style={{ ...tdStyle, textAlign: "center", fontSize: 16, color: COLORS.textDim }}>{isOpen ? "▾" : "▸"}</td>
                  <td style={tdStyle}><div style={{ fontWeight: 600, color: COLORS.text }}>{t.origen_desc || t.id_bodega_origen}</div></td>
                  <td style={tdStyle}><div style={{ fontWeight: 600, color: COLORS.text }}>{t.destino_desc || t.id_bodega_destino}</div></td>
                  <td style={tdStyle}><StatusBadge status={t.estado} /></td>
                  <td style={{ ...tdStyle, ...mono, color: COLORS.accent }}>{t.score_prioridad?.toFixed(1)}</td>
                  <td style={{ ...tdStyle, ...mono }}>
                    {aTrasladar.length} items
                    {seQuedan.length > 0 && (
                      <span style={{ color: COLORS.textDim, fontSize: 11 }}> +{seQuedan.length} con saldo</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: COLORS.textDim, fontSize: 12 }}>{t.fecha_sugerencia?.slice(0, 10)}</td>
                  <td style={tdStyle} onClick={e => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {est === "sugerido" && <>
                        <button onClick={() => handleAction(t.id_traslado, "aprobar")} style={btnSmallSuccess}>Aprobar</button>
                        <button onClick={() => handleAction(t.id_traslado, "cancelar")} style={btnSmallDanger}>Cancelar</button>
                      </>}
                      {est === "aprobado" && <>
                        <button onClick={() => handleAction(t.id_traslado, "recibir")} style={btnRecibir}>Recibir</button>
                        <button onClick={() => handleAction(t.id_traslado, "cancelar")} style={btnSmallDanger}>Cancelar</button>
                      </>}
                      {est === "recibido" && <span style={{ color: COLORS.success, fontSize: 11 }}>Completado</span>}
                      {est === "recibido_por_tiempo" && <span style={{ color: COLORS.warning, fontSize: 11 }}>Recibido (tiempo)</span>}
                    </div>
                  </td>
                </tr>,
                isOpen && (
                  <tr key={`d-${i}`}>
                    <td colSpan={8} style={{ padding: 0 }}>
                      <div style={{ background: COLORS.surfaceHover, borderBottom: `2px solid ${COLORS.primary}33`, padding: "12px 20px 12px 52px" }}>
                        {detalle.length > 0 ? (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                              <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
                                {t.origen_desc || t.id_bodega_origen} → {t.destino_desc || t.id_bodega_destino} — {aTrasladar.length} artículo(s) a trasladar
                                {seQuedan.length > 0 && ` · ${seQuedan.length} con saldo en origen que no se trasladan`}
                              </span>
                              <CSVButton
                                data={detalle.map(d => ({
                                  id_articulo: d.id_articulo,
                                  en_traslado: esExtra(d) ? "N" : "S",
                                  cantidad: d.cantidad,
                                  saldo_origen: d.saldo_origen ?? "",
                                  disponible_origen: d.stock_origen ?? "",
                                  saldo_destino: d.saldo_destino ?? "",
                                  razon: d.razon || "",
                                  ...Object.fromEntries(clasFilters.filter(f => f.id_clasificador).map((f, fi) => [f.id_clasificador, d[`nombre_tipo_${fi}`] || ""])),
                                }))}
                                filename={`traslado_${t.id_bodega_origen}_a_${t.id_bodega_destino}`} label="CSV" />
                            </div>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead><tr style={{ background: `${COLORS.primary}12` }}>
                                <th style={{ ...thStyle, padding: "6px 12px" }}>Artículo</th>
                                {clasFilters.map((f, fi) => f.id_clasificador && (
                                  <th key={fi} style={{ ...thStyle, padding: "6px 12px" }}>{f.id_clasificador}</th>
                                ))}
                                <th style={{ ...thStyle, padding: "6px 12px" }}>Cantidad</th>
                                <th style={{ ...thStyle, padding: "6px 12px", textAlign: "right" }} title="Saldo actual del ERP en la bodega origen (antes del traslado)">Saldo Origen</th>
                                <th style={{ ...thStyle, padding: "6px 12px", textAlign: "right" }} title="Saldo en origen menos lo ya comprometido en traslados aprobados">Disp. Origen</th>
                                <th style={{ ...thStyle, padding: "6px 12px", textAlign: "right" }} title="Saldo actual del ERP en la bodega destino (antes del traslado)">Saldo Destino</th>
                                <th style={{ ...thStyle, padding: "6px 12px" }}>Razón</th>
                              </tr></thead>
                              <tbody>
                                {detalle.map((d, di) => {
                                  const extra = esExtra(d);
                                  return (
                                    <tr key={di} style={{ borderBottom: `1px solid ${COLORS.border}`, background: extra ? `${COLORS.warning}0A` : "transparent" }}>
                                      <td style={{ ...tdStyle, padding: "6px 12px", ...mono, fontSize: 12, color: extra ? COLORS.textMuted : COLORS.text }}>{d.id_articulo}</td>
                                      {clasFilters.map((f, fi) => f.id_clasificador && (
                                        <td key={fi} style={{ ...tdStyle, padding: "6px 12px", fontSize: 11, color: COLORS.textMuted }}>
                                          {d[`nombre_tipo_${fi}`] || "—"}
                                        </td>
                                      ))}
                                      <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "center", color: extra ? COLORS.textDim : COLORS.text }}>{Number(d.cantidad ?? 0)}</td>
                                      <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: COLORS.text }}>{d.saldo_origen != null ? Number(d.saldo_origen).toLocaleString() : "—"}</td>
                                      <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: COLORS.text }}>{d.stock_origen != null ? Number(d.stock_origen).toLocaleString() : "—"}</td>
                                      <td style={{ ...tdStyle, padding: "6px 12px", ...mono, textAlign: "right", color: COLORS.text }}>{d.saldo_destino != null ? Number(d.saldo_destino).toLocaleString() : "—"}</td>
                                      <td style={{ ...tdStyle, padding: "6px 12px", fontSize: 11, color: COLORS.textDim }}>
                                        {extra
                                          ? <span style={{ padding: "2px 8px", borderRadius: 10, background: COLORS.warningSoft, color: COLORS.warning, fontSize: 10, fontWeight: 600 }}>No se traslada</span>
                                          : (d.razon || "—")}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </>
                        ) : (
                          <span style={{ color: COLORS.textDim, fontSize: 13 }}>Sin artículos que coincidan con el filtro de clasificadores</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ),
              ];
            })
          }</tbody>
        </table>
      </div></div>

      {traslados && traslados.total > PAGE_SIZE && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 4px 4px" }}>
          <span style={{ color: COLORS.textDim, fontSize: 13 }}>
            {traslados.total} traslados · página {page} de {Math.ceil(traslados.total / PAGE_SIZE)}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>«</button>
            <button onClick={() => setPage(p => p - 1)} disabled={page === 1} style={{ ...btnSecondary, padding: "4px 10px", opacity: page === 1 ? 0.4 : 1 }}>‹</button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(traslados.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(traslados.total / PAGE_SIZE) ? 0.4 : 1 }}>›</button>
            <button onClick={() => setPage(Math.ceil(traslados.total / PAGE_SIZE))} disabled={page >= Math.ceil(traslados.total / PAGE_SIZE)} style={{ ...btnSecondary, padding: "4px 10px", opacity: page >= Math.ceil(traslados.total / PAGE_SIZE) ? 0.4 : 1 }}>»</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrasladosMultiClasSaldoPage;
